import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  CatalogItem,
  CatalogsList,
  CatalogTableItem,
  Country,
  FormSection,
  language,
  NonPostalCountries,
  operationResponse,
  ShipmentIdentifier,
  SyncReport,
} from '@shared';
import { catchError, map, Observable, shareReplay, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AppAuthServiceService } from '../app-auth-service.service';
import { UserPreferencesService } from './user-preferences.service';

@Injectable({
  providedIn: 'root',
})
export class CatalogService {
  private catalogsPath: string = `${environment.apiBasePath}${environment.catalogsPath}`;
  private syncPath: string = `${environment.apiBasePath}/sync`;

  private $cache: {
    inquiryCountry?: Observable<CatalogItem[]>;
    priority?: Observable<CatalogItem[]>;
    receivedFrom?: Observable<CatalogItem[]>;
    contactType?: Observable<CatalogItem[]>;
    preferredCommunicationChannelctType?: Observable<CatalogItem[]>;
    language?: Observable<CatalogItem[]>;
    userLanguage?: Observable<CatalogItem[]>;
    shipmentIdentifier?: Observable<ShipmentIdentifier[]>;
    country?: Observable<Country[]>;
    formSchema?: Observable<{ value: FormSection[] }>;
  } = {};

  private preferences: {
    language: string;
  } = {
      language: language.default,
    };

  constructor(
    private http: HttpClient,
    private userPreferences: UserPreferencesService,
    private appAuthService: AppAuthServiceService
  ) {
    const accountId = this.appAuthService.account.homeAccountId || '';
    this.userPreferences.getUserPreferences(accountId).subscribe((preferences) => {
      if (!preferences.error) {
        this.preferences.language = preferences.language;
      }
      this.preferences.language ??= language.default;
    });
  }

  public setLanguage(language: string): void {
    if (language && language.toLowerCase() !== this.preferences.language.toLowerCase()) {
      this.preferences.language = language;
      this.$cache = {};
    }
  }

  public nonPostalCountries(): Observable<NonPostalCountries[]> {
    return this.http.get<NonPostalCountries[]>(
      `${this.catalogsPath}/NonPostalCountries?Language=${this.preferences.language}`
    );
  }

  public inquiryCountry(): Observable<CatalogItem[]> {
    if (!this.$cache.inquiryCountry) {
      this.$cache.inquiryCountry = this.http
        .get<CatalogItem[]>(`${this.catalogsPath}/InquiryCountry?Language=${this.preferences.language}`)
        .pipe(shareReplay(1));
    }
    return this.$cache.inquiryCountry;
  }

  public businessUnit(): Observable<CatalogItem[]> {
    return this.http.get<CatalogItem[]>(`${this.catalogsPath}/BusinessUnit?Language=${this.preferences.language}`);
  }

  public businessUnitByCountry(country: string): Observable<CatalogItem[]> {
    return this.http
      .get<CatalogItem[]>(`${this.catalogsPath}/BusinessUnitByCountry/${country}?Language=${this.preferences.language}`)
      .pipe(catchError(this.handleError),
        map((response) => {
          response.forEach((item) => {
            item.id = `${country}.${item.id}`;
          });
          return response;
        }),
        catchError(this.handleError)
      );
  }

  public categoryByBusinessUnit(businessUnit: string): Observable<CatalogItem[]> {
    return this.http
      .get<CatalogItem[]>(
        `${this.catalogsPath}/CategoryByBusinessUnit/${businessUnit}?Language=${this.preferences.language}`
      )
      .pipe(catchError(this.handleError),
        map((response) => {
          response.forEach((item) => {
            item.id = `${businessUnit}.${item.id}`;
          });
          return response;
        }),
        catchError(this.handleError)
      );
  }

  public subcategoryByCategory(category: string): Observable<CatalogItem[]> {
    return this.http
      .get<CatalogItem[]>(
        `${this.catalogsPath}/SubCategoryByCategory/${category}?Language=${this.preferences.language}`
      )
      .pipe(catchError(this.handleError),
        map((response) => {
          response.forEach((item) => {
            item.id = `${category}.${item.id}`;
          });
          return response;
        }),
        catchError(this.handleError)
      );
  }

  private handleError(error: HttpErrorResponse) {
    if (error.status === 500) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('Server Error:', error.error);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong.
      console.error(
        `Backend returned code ${error.status}, body was: `, error.error);
    }
    // Return an observable with a user-facing error message.
    return throwError(() => new Error('Something bad happened; please try again later.'));
  }

  public priority(): Observable<CatalogItem[]> {
    if (!this.$cache.priority) {
      this.$cache.priority = this.http
        .get<CatalogItem[]>(`${this.catalogsPath}/Priority?Language=${this.preferences.language}`)
        .pipe(shareReplay(1));
    }
    return this.$cache.priority;
  }

  public receivedFrom(): Observable<CatalogItem[]> {
    if (!this.$cache.receivedFrom) {
      this.$cache.receivedFrom = this.http
        .get<CatalogItem[]>(`${this.catalogsPath}/ReceivedFrom?Language=${this.preferences.language}`)
        .pipe(shareReplay(1));
    }
    return this.$cache.receivedFrom;
  }

  public contactType(): Observable<CatalogItem[]> {
    if (!this.$cache.contactType) {
      this.$cache.contactType = this.http
        .get<CatalogItem[]>(`${this.catalogsPath}/ContactType?Language=${this.preferences.language}`)
        .pipe(shareReplay(1));
    }
    return this.$cache.contactType;
  }

  public preferredCommunicationChannel(): Observable<CatalogItem[]> {
    if (!this.$cache.preferredCommunicationChannelctType) {
      this.$cache.preferredCommunicationChannelctType = this.http
        .get<CatalogItem[]>(`${this.catalogsPath}/PreferredCommunicationChannel?Language=${this.preferences.language}`)
        .pipe(shareReplay(1));
    }
    return this.$cache.preferredCommunicationChannelctType;
  }

  public language(): Observable<CatalogItem[]> {
    if (!this.$cache.language) {
      this.$cache.language = this.http
        .get<CatalogItem[]>(`${this.catalogsPath}/UserLanguage?Language=${this.preferences.language}`)
        .pipe(shareReplay(1));
    }
    return this.$cache.language;
  }

  public userLanguage(): Observable<CatalogItem[]> {
    if (!this.$cache.userLanguage) {
      this.$cache.userLanguage = this.http
        .get<CatalogItem[]>(`${this.catalogsPath}/UserLanguage?Language=${this.preferences.language}`)
        .pipe(shareReplay(1));
    }
    return this.$cache.userLanguage;
  }

  public shipmentIdentifier(): Observable<ShipmentIdentifier[]> {
    if (!this.$cache.shipmentIdentifier) {
      this.$cache.shipmentIdentifier = this.http
        .get<ShipmentIdentifier[]>(`${this.catalogsPath}/ShipmentIdentifier?Language=${this.preferences.language}`)
        .pipe(shareReplay(1));
    }
    return this.$cache.shipmentIdentifier;
  }

  public stateProvince(): Observable<CatalogItem[]> {
    return this.http.get<CatalogItem[]>(`${this.catalogsPath}/StateProvince?Language=${this.preferences.language}`);
  }

  public stateProvinceByCountry(countryAbbreviation: string): Observable<CatalogItem[]> {
    return this.http.get<CatalogItem[]>(
      `${this.catalogsPath}/StateProvince/${countryAbbreviation}?Language=${this.preferences.language}`
    );
  }

  public country(): Observable<Country[]> {
    if (!this.$cache.country) {
      this.$cache.country = this.http
        .get<Country[]>(`${this.catalogsPath}/Country?Language=${this.preferences.language}`)
        .pipe(shareReplay(1));
    }
    return this.$cache.country;
  }

  public formSchema(lang: string): Observable<{ value: FormSection[] }> {
    if (!this.$cache.formSchema) {
      this.$cache.formSchema = this.http.get<{ value: FormSection[] }>(`${this.catalogsPath}/FormTemplate/${lang}`);
    }
    return this.$cache.formSchema;
  }

  public category(): Observable<CatalogItem[]> {
    return this.http.get<CatalogItem[]>(`${this.catalogsPath}/Category?Language=${this.preferences.language}`);
  }

  public syncToCEC(): Observable<any> {
    return this.http.post(`${this.syncPath}`, {}, { observe: 'response' });
  }

  /**
   * getCatalogsList
   */
  public getCatalogsList(): Observable<Array<CatalogsList>> {
    return this.http.get<Array<CatalogsList>>(`${this.catalogsPath}/list`);
  }

  /**
   * getCatalog
   */
  public getCatalog(id: string): Observable<CatalogTableItem[]> {
    return this.http.get<CatalogTableItem[]>(`${this.catalogsPath}/edit/${id}/list`);
  }

  public getLatestSyncStatus(): Observable<SyncReport> {
    return this.http.get<SyncReport[]>(`${this.syncPath}/latest`).pipe(map((response: SyncReport[]) => response[0]));
  }

  public getSyncReport(): Observable<SyncReport[]> {
    return this.http.get<SyncReport[]>(`${this.syncPath}`);
  }

  public getFailedCases(page: number, limitPage: number, groupBatchId?: string): Observable<SyncReport[]> {
    return this.http.get<SyncReport[]>(`${this.syncPath}/${groupBatchId}/${page}/${limitPage}`);
  }

  public getHistory(pagelimit: number, latestBatchGroupId?: string): Observable<SyncReport[]> {
    return this.http.get<SyncReport[]>(
      `${this.syncPath}/history${latestBatchGroupId ? `/${latestBatchGroupId}` : ''}/${pagelimit}`
    );
  }

  public getFirstPageHistory(pagelimit: number): Observable<SyncReport[]> {
    return this.http.get<SyncReport[]>(`${this.syncPath}/history/firsts/${pagelimit}`);
  }

  public getSummaryCases(): Observable<number> {
    return this.http.get<{ itemsCount: number }>(`${this.syncPath}/summary`).pipe(map((item) => item.itemsCount));
  }

  public updateCatalog(id: string, data: any): Observable<operationResponse | operationResponse[]> {
    return this.http.put<operationResponse>(`${this.catalogsPath}/edit/${id}`, data);
  }
}
